import { useSearchParams } from "react-router"
import { useCallback } from "react"

/**
 * Drop-in replacement for useState that syncs with a URL search param.
 * When value === defaultValue, the param is removed to keep URLs clean.
 * Uses replace navigation to avoid polluting browser history on every click.
 */
export function useSearchParamState(
  key: string,
  defaultValue: string,
): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const value = searchParams.get(key) ?? defaultValue

  const setValue = useCallback(
    (newValue: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (newValue === defaultValue) {
            next.delete(key)
          } else {
            next.set(key, newValue)
          }
          return next
        },
        { replace: true },
      )
    },
    [key, defaultValue, setSearchParams],
  )

  return [value, setValue]
}
